import { useState, useRef } from "react";
import Input from "antd/es/input/Input";
import { Breadcrumb, Button, Col, Row, Typography, Checkbox, Slider, InputNumber } from "antd";
import Autocomplete from "react-google-autocomplete";
import Loader from "../../components/loader/loader";
import { AudioPlaceholder, imagePlaceholder, videoIcon } from "../../assets";
import "./styles.css";
import { GreenNotify, RedNotify, uploadMediaToS3 } from "../../helper/helper";
import routes from "../../api/routes";
import { callApi } from "../../api/apiCaller";
import { useNavigate } from "react-router-dom";

const GeneralPost = () => {
  const navigate = useNavigate();
  const [isloading, setIsLoading] = useState(false);

  const [content, setContent] = useState([]);
  const [text, setText] = useState("");

  // --- REFS ---
  const fileInputImageRef = useRef(null);
  const fileInputVideoRef = useRef(null);
  const fileInputAudioRef = useRef(null); // Added dedicated ref for Audio
  const inputRef = useRef(null);

  const [selectedOption, setSelectedOption] = useState(null);
  const [restricted, setRestricted] = useState(false);
  const [video, setVideo] = useState(null);
  const [coordinates, setCoordinates] = useState([-11.779889, 8.46055]);
  const [radius, setRadius] = useState(4);
  const [targetGenders, setTargetGenders] = useState([]);
  const [isAgeFilterEnabled, setIsAgeFilterEnabled] = useState(false);
  const [ageRange, setAgeRange] = useState([18, 65]);
  const googleAPIKey = "AIzaSyDIpm6qlncXP5fVBDmmTovsA_mMJTXzjxM";

  // --- HELPER FUNCTIONS ---
  const uploadImage = (url) => {
    const imageContent = {
      link: url,
      type: "image",
    };
    setContent([imageContent]);
  };

  const uploadVideo = (url) => {
    const videoContent = {
      link: url,
      type: "video",
    };
    setVideo(url);
    setContent([videoContent]);
  };

  const pickImageFile = () => {
    fileInputImageRef.current.click();
  };

  const pickVideoFile = () => {
    fileInputVideoRef.current.click();
  };

  // Added handler for picking audio
  const pickAudioFile = () => {
    fileInputAudioRef.current.click();
  };

  const onChange = (e) => {
    setRestricted(e.target.checked);
  };

  const onChangeComplete = (value) => {
    setRadius(value);
  };

  const tooltipFormatter = (value) => `${value} Km`;
  const ageTooltipFormatter = (value) => `${value} years`;

  const handlePlaceSelected = (place) => {
    if (place.geometry) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setCoordinates([lng, lat]);
    }
  };

  const disableValue = text.length === 0 && !content.length && !video;

  const updateAgeRangeValue = (index, value) => {
    if (value === null || value === undefined) return;
    const nextRange = [...ageRange];
    nextRange[index] = value;

    if (index === 0 && value > nextRange[1]) {
      nextRange[1] = value;
    }

    if (index === 1 && value < nextRange[0]) {
      nextRange[0] = value;
    }

    setAgeRange(nextRange);
  };

  const buildTargetFilters = () => {
    const targetFilters = {};

    if (targetGenders.length > 0) {
      targetFilters.genders = targetGenders;
    }

    if (isAgeFilterEnabled) {
      targetFilters.ageRange = {
        min: ageRange[0],
        max: ageRange[1],
      };
    }

    return Object.keys(targetFilters).length > 0 ? targetFilters : undefined;
  };

  // --- UPLOAD HANDLERS ---
  const onImageChange = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    setIsLoading(true);
    try {
      const url = await uploadMediaToS3(files[0], false);
      uploadImage(url);
    } catch (error) {
      RedNotify("Image upload failed.");
      console.error("Image upload error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onVideoChange = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    const videoFile = files[0];

    setIsLoading(true);

    try {
      const videoUrl = await uploadMediaToS3(videoFile, false);
      uploadVideo(videoUrl);

      const thumbnailBlob = await generateThumbnailFromVideo(videoFile);

      const thumbnailFile = new File([thumbnailBlob], "thumbnail.jpg", {
        type: "image/jpeg",
      });

      const thumbnailUrl = await uploadMediaToS3(thumbnailFile, false);

      setContent([
        {
          link: videoUrl,
          type: "video",
          thumbnail: thumbnailUrl,
        },
      ]);
    } catch (error) {
      RedNotify("Video upload or thumbnail generation failed.");
      console.error("Video or thumbnail upload error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onAudioChange = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    if (!file.type.startsWith("audio/")) {
      RedNotify("Please select a valid audio file");
      return;
    }
    setIsLoading(true);
    try {
      const url = await uploadMediaToS3(file, false);
      setContent([{ link: url, type: "audio" }]);
    } catch (error) {
      console.log("ERROR:", error);
      RedNotify("Audio upload failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const uploadPost = () => {
    const targetFilters = buildTargetFilters();
    const data = {
      content,
      text,
      restrictedArea: !restricted,
      restrictedRadius: radius,
      location: {
        type: "Point",
        coordinates,
        address: "",
        description: "",
      },
      tags: ["#admin"],
      ...(targetFilters && { targetFilters }),
    };

    const getRes = (res) => {
      if (res.status === 201) {
        GreenNotify(res.message);
        navigate("/");
      } else {
        RedNotify(res.message);
      }
    };

    callApi("POST", routes.adminPost, data, setIsLoading, getRes, (error) => {
      console.log("error", error);
    });
  };

  const generateThumbnailFromVideo = (file) => {
    return new Promise((resolve, reject) => {
      if (!(file instanceof Blob)) {
        return reject("Invalid file provided. Not a Blob/File.");
      }

      const video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true;
      video.playsInline = true;

      try {
        video.src = URL.createObjectURL(file);
      } catch (e) {
        return reject("Error creating video URL: " + e.message);
      }

      video.onloadeddata = () => {
        video.currentTime = 1;
      };

      video.onseeked = () => {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject("Canvas toBlob failed.");
          }
        }, "image/jpeg");
      };

      video.onerror = () => {
        reject("Video element error.");
      };
    });
  };

  const handleChangeAddSelection = (type) => {
    setSelectedOption(type);
    if (content.length > 0) setContent([]);
  };

  return (
    <div style={{ marginBottom: "2rem" }}>
      <Loader loading={isloading} />
      <Breadcrumb
        separator=">"
        className="bread-crumb"
        items={[
          {
            title: "Home",
          },
          {
            title: "General Post",
          },
        ]}
      />

      <div className="configure-server-roles-main-heading-container">
        <h1>Post</h1>
      </div>

      <h1 style={{ marginTop: "2rem", marginBottom: "2rem" }}>Enter Location</h1>

      <div style={{ marginTop: "2rem", marginBottom: "2rem" }}>
        <Checkbox className="custom-checkbox" onChange={onChange}>
          For All User
        </Checkbox>
      </div>

      {!restricted && (
        <>
          <Autocomplete
            style={{ width: "350px", padding: "5px", fontSize: "14px" }}
            ref={inputRef}
            apiKey={googleAPIKey}
            onPlaceSelected={handlePlaceSelected}
            options={{ types: ["geocode", "establishment"] }}
            defaultValue="Sierra Leone"
          />

          <Typography.Title level={5} style={{ marginTop: "2rem" }}>
            Select Radius Range
          </Typography.Title>

          <Slider
            style={{ width: "35rem" }}
            defaultValue={4}
            onChangeComplete={onChangeComplete}
            tooltip={{ formatter: tooltipFormatter }}
            max={300}
            min={4}
          />
        </>
      )}

      <Typography.Title level={5} style={{ marginTop: "2rem" }}>
        Target Sex
      </Typography.Title>
      <Checkbox.Group options={["Male", "Female"]} value={targetGenders} onChange={setTargetGenders} />

      <Typography.Title level={5} style={{ marginTop: "2rem" }}>
        Target Age
      </Typography.Title>
      <Checkbox
        className="custom-checkbox"
        checked={isAgeFilterEnabled}
        onChange={(e) => setIsAgeFilterEnabled(e.target.checked)}
      >
        Filter by age range
      </Checkbox>

      {isAgeFilterEnabled && (
        <div style={{ marginTop: "1rem" }}>
          <Slider
            range
            style={{ width: "35rem" }}
            value={ageRange}
            onChange={setAgeRange}
            tooltip={{ formatter: ageTooltipFormatter }}
            max={100}
            min={13}
          />
          <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
            <InputNumber
              min={13}
              max={100}
              value={ageRange[0]}
              onChange={(value) => updateAgeRangeValue(0, value)}
              addonBefore="Min"
            />
            <InputNumber
              min={13}
              max={100}
              value={ageRange[1]}
              onChange={(value) => updateAgeRangeValue(1, value)}
              addonBefore="Max"
            />
          </div>
        </div>
      )}

      {/* --- MEDIA TYPE SELECTION BUTTONS --- */}
      <div className="options-container" style={{ display: "flex", gap: "2rem", marginTop: "2rem" }}>
        <div
          className={`option ${selectedOption === "image" ? "selected" : ""}`}
          onClick={() => handleChangeAddSelection("image")}
        >
          <Button>Add Image</Button>
        </div>
        <div
          className={`option ${selectedOption === "video" ? "selected" : ""}`}
          onClick={() => handleChangeAddSelection("video")}
        >
          <Button>Add Video</Button>
        </div>

        <div
          className={`option ${selectedOption === "audio" ? "selected" : ""}`}
          onClick={() => handleChangeAddSelection("audio")}
        >
          <Button>Add Audio</Button>
        </div>
      </div>

      {/* --- IMAGE UPLOAD SECTION --- */}
      {selectedOption === "image" && (
        <div className="image-upload-container">
          <img
            onClick={pickImageFile}
            alt="image"
            src={
              typeof content[0]?.link === "string" && content[0]?.link && content[0]?.type === "image"
                ? content[0].link
                : imagePlaceholder
            }
          />
          <input
            type="file"
            ref={fileInputImageRef}
            accept="image/*"
            style={{ display: "none" }}
            onChange={onImageChange}
          />
        </div>
      )}

      {/* --- VIDEO UPLOAD SECTION --- */}
      {selectedOption === "video" && (
        <div className="video-upload-container">
          {video ? (
            <video controls src={video} style={{ width: "25rem", height: "20rem" }} />
          ) : (
            <img
              onClick={pickVideoFile}
              alt="Upload Video"
              src={videoIcon}
              style={{
                cursor: "pointer",
                maxWidth: "100%",
                maxHeight: "100%",
              }}
            />
          )}
          <input
            type="file"
            ref={fileInputVideoRef}
            style={{ display: "none" }}
            onChange={onVideoChange}
            accept="video/*"
          />
        </div>
      )}

      {/* --- AUDIO UPLOAD SECTION (UPDATED) --- */}
      {selectedOption === "audio" && (
        <div className="image-upload-container">
          {/* 2. Audio Player Preview (Only if content exists) */}
          {/* 1. Click Trigger */}
          {content.length > 0 && content[0].type === "audio" ? (
            <div style={{ marginTop: "10px" }}>
              <audio controls src={content[0].link} />
            </div>
          ) : (
            <img
              onClick={pickAudioFile}
              alt="audio file"
              src={AudioPlaceholder}
              style={{
                cursor: "pointer",
              }}
            />
          )}

          {/* 3. Hidden Input */}
          <input
            type="file"
            ref={fileInputAudioRef}
            accept=".mp3,.wav,.aac,.m4a,.ogg,.flac,.opus"
            style={{ display: "none" }}
            onChange={onAudioChange}
          />
        </div>
      )}

      <Row gutter={216}>
        <Col span={8}>
          <Typography.Title level={5} style={{ marginTop: "2rem" }}>
            Text
          </Typography.Title>
          <Input
            style={{ marginTop: "1rem", height: "5rem" }}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Notification"
          />
        </Col>
      </Row>

      <Button disabled={disableValue} type="primary" style={{ marginTop: "2rem" }} onClick={uploadPost}>
        Upload
      </Button>
    </div>
  );
};

export default GeneralPost;
